import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { ConversationMember } from "@/models/ai-chat/ConversationMember";
import { SmallUserDTO } from "@/models/SmallUserDTO";
import LazoUserService from "@/services/LazoUserService";
import { Box, BoxProps, styled, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mention, MentionItem, MentionsInput, SuggestionDataItem } from "react-mentions";
import { MentionsFn } from "./ChatBar";
import { SuggestionItem } from "./SuggestionItem";

const debouncer = new Debouncer(200);

export type ExtendedSuggestionDataItem = SuggestionDataItem & {
  lazoEmployee: boolean;
  firstName: string;
  lastName: string;
  email: string;
};

type SuggestedUsersTextFieldProps = {
  conversationExternalId: string;
  onMentionsChange: MentionsFn;
  containerSx?: BoxProps["sx"];
  mentionStyle?: any;
  hideTo?: boolean;
  placeholder?: string;
  previewMembers?: ConversationMember[];
};

const TRIGGER_REGEXP = /(([A-Za-z0-9]+))/;

function renderPreviewMembers(pm: ConversationMember[]): string {
  if (!pm || pm.length === 0) return "";
  let result = "";

  for (let i = 0; i < pm.length; i++) {
    const current = pm[i];
    result += `@[${current.user.firstName} ${current.user.lastName}](${current.user.externalId!}) `;
  }

  return result;
}

function SuggestedUsersTextField(props: SuggestedUsersTextFieldProps) {
  const { t } = useTranslation("chat");
  const currentUserExternalId = AuthService.getUser().externalId;
  const [value, setValue] = useState<string>("");
  const [suggestedUsers, setSuggestedUsers] = useState<SmallUserDTO[] | null>(null);

  useEffect(() => {
    if (!props.previewMembers) {
      return;
    }
    setValue(renderPreviewMembers(props.previewMembers.filter((m) => m.user.externalId !== currentUserExternalId)));
  }, [props.previewMembers]);

  const currentTenantMembership = useTenantMemberships().currentTenantMembership;

  function fetchSuggestedUsers(term: string, callback: (data: SuggestionDataItem[]) => void) {
    if (!currentTenantMembership) return;
    debouncer.executeLastCall(() => {
      LazoUserService.getChatUserSuggestions(term, currentTenantMembership.tenant.externalId, props.conversationExternalId || null).then(
        (r) => {
          r.onSuccess((suggs) => {
            const filtered = suggs.filter((u) => u.externalId !== currentUserExternalId);
            setSuggestedUsers((prev) => {
              const n = filtered.filter((f) => !suggestedUsers || suggestedUsers.find((u) => u.externalId === f.externalId) === undefined);
              if (prev) {
                return [...prev, ...n];
              } else return n;
            });
            callback(mapToExtendedSuggestedUser(filtered));
          });
          r.onError(() => callback([]));
        }
      );
    });
  }

  function handleMentionsChange(ev: { target: { value: string } }, mentions: MentionItem[]) {
    setValue(ev.target.value);
    const ids = mentions.map((m) => m.id);
    debouncer.executeLastCall(() => {
      props.onMentionsChange(
        ev,
        mentions,
        suggestedUsers?.filter((su) => ids.includes(su.externalId as string))
      );
    });
  }

  return (
    <ToBar sx={props.containerSx}>
      {!props.hideTo && <ToText>{t("suggestedUsersTextField.toLabel")}</ToText>}
      <MentionsInput
        value={value}
        onChange={(ev, _, __, mentions) => handleMentionsChange(ev, mentions)}
        singleLine={true}
        placeholder={props.placeholder ? props.placeholder : t("suggestedUsersTextField.placeholder")}
        style={{ ...MentionsInputStyle, ...props.mentionStyle }}
      >
        <Mention
          trigger={TRIGGER_REGEXP}
          markup={"@[__display__](__id__)"}
          data={fetchSuggestedUsers}
          appendSpaceOnAdd
          renderSuggestion={(entry) => {
            const extendedEntry = entry as ExtendedSuggestionDataItem;
            return (
              <SuggestionItem
                entry={{
                  id: entry.id,
                  value: entry.display,
                  email: extendedEntry.email,
                  lazoEmployee: extendedEntry.lazoEmployee,
                }}
                containerSx={{ backgroundColor: "transparent" }}
              />
            );
          }}
          displayTransform={(_, d) => "@" + d}
        />
      </MentionsInput>
    </ToBar>
  );
}

function mapToExtendedSuggestedUser(users: SmallUserDTO[]): ExtendedSuggestionDataItem[] {
  if (!users || users.length === 0) return [];
  const suggested: ExtendedSuggestionDataItem[] = users.map((u) => ({
    id: u.externalId,
    display: u.fullName,
    lazoEmployee: u.isLazoEmployee,
    firstName: u.firstName,
    lastName: u.lastName,
    email: u.email,
  }));
  return suggested;
}

const ToBar = styled(Box)(() => ({
  padding: "1rem 1.5rem",
  gap: "0.5rem",
  width: "100%",
  position: "sticky",
  minHeight: "3.9rem",
  height: "auto",
  flexWrap: "wrap",
  display: "flex",
  alignItems: "center",
  top: 0,
  backgroundColor: Colors.WHITE,
  zIndex: 2,
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
}));

const ToText = styled(Typography)(() => ({
  display: "flex",
  color: Colors.LIGHT_GRAY_8A8E93,
  alignItems: "center",
  fontSize: "0.875rem",
  fontWeight: 400,
  height: "100%",
}));

const MentionsInputStyle = {
  display: "flex",
  alignItems: "center",
  fontSize: "0.875rem",
  fontWeight: 400,
  height: "100%",
  flex: 1,
  maxWidth: "100%",
  flexWrap: "wrap",
  "&singleLine": {
    control: {
      flex: 1,
    },
    highlighter: {
      position: "absolute",
      border: "none",
      outline: "none",
    },
    input: {
      position: "relative",
      flex: 1,
      fontWeight: 500,
      border: "none",
      outline: "none",
    },
  },
  suggestions: {
    top: 16,
    width: "100%",
    list: {
      overflow: "hidden",
      maxHeight: "15rem",
      overflowY: "auto",
      borderRadius: "0.25rem",
      padding: "0.5rem 0",
      backgroundColor: "white",
      border: `1px solid ${Colors.LIGHT_GRAY}`,
      boxShadow: "0 0 15px rgba(0, 0, 0, 0.2)",
      fontSize: "0.875rem",
      fontWeight: "500",
    },
    item: {
      fontSize: "0.75rem",
      fontWeight: 500,
      padding: "0.5rem 1rem",
      borderBottom: 0,
      "&focused": {
        backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
      },
    },
  },
};

export default SuggestedUsersTextField;
